/*
 * Turns the accordions in sections/mh-tabs.liquid into a tab strip (SPEC-PHASE1-RETHEME §7.5).
 *
 * The server sends `<details>` rows. This reads their summaries, builds a strip of real <button>s
 * above them, forces every row open (the heading is hidden in the enhanced state, so a closed row
 * would be unreachable), and shows one row at a time. The `data-mh-enhanced` attribute it sets is
 * what unlocks the tab CSS — so if this file never loads, or throws, the page keeps the accordions
 * it was served rather than becoming a row of dead words.
 *
 * The strip follows the WAI-ARIA tabs pattern: roving tabindex, arrow keys, Home and End.
 */
class MhTabs extends HTMLElement {
  connectedCallback() {
    this.panels = [...this.querySelectorAll('details.mh-acc__item')];
    if (this.panels.length < 2) return;

    const titles = this.panels.map(
      (p, i) => p.querySelector('.mh-acc__title')?.textContent.trim() || `Bahagian ${i + 1}`
    );

    this.strip = document.createElement('div');
    this.strip.className = 'mh-tabs__strip';
    this.strip.setAttribute('role', 'tablist');

    this.tabs = titles.map((title, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'mh-tabs__tab';
      b.textContent = title;
      b.setAttribute('role', 'tab');
      b.id = `${this.panelId(i)}-tab`;
      b.setAttribute('aria-controls', this.panelId(i));
      b.addEventListener('click', () => this.select(i));
      b.addEventListener('keydown', (e) => this.onKey(e, i));
      this.strip.appendChild(b);
      return b;
    });

    this.panels.forEach((p, i) => {
      // Open for good: in the enhanced state the summary is hidden, so a shut row could not be
      // reopened. Visibility is carried by `hidden` on the row instead.
      p.open = true;
      p.id = this.panelId(i);
      p.setAttribute('role', 'tabpanel');
      p.setAttribute('aria-labelledby', `${this.panelId(i)}-tab`);
    });

    this.prepend(this.strip);
    this.setAttribute('data-mh-enhanced', '');
    this.select(0);
  }

  panelId(i) {
    if (!this._base) {
      this._base = `mh-tabpanel-${Math.abs(hashOf(this.closest('[id]')?.id || 'mh-tabs'))}`;
    }
    return `${this._base}-${i}`;
  }

  select(i) {
    this.panels.forEach((p, n) => {
      p.hidden = n !== i;
    });
    this.tabs.forEach((t, n) => {
      t.setAttribute('aria-selected', String(n === i));
      // Roving tabindex: only the selected tab is a tab stop, arrows move between them.
      t.tabIndex = n === i ? 0 : -1;
    });
  }

  onKey(e, i) {
    const last = this.tabs.length - 1;
    let next = null;
    if (e.key === 'ArrowRight') next = i === last ? 0 : i + 1;
    else if (e.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    this.select(next);
    this.tabs[next].focus();
  }
}

// A stable id per section instance, so two tab sets on one page cannot collide.
function hashOf(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return h;
}

if (!customElements.get('mh-tabs')) {
  customElements.define('mh-tabs', MhTabs);
}
